import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, serverTimestamp, getDocs, query, where, doc, setDoc, getDoc } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { getAllCourses } from '@/utils/firestoreService';
import Layout from '@/components/layout/Layout';
import { format } from 'date-fns';

// Remove hardcoded AREAS since we'll fetch from Firestore
const CAR_TYPES = ['Manual', 'Automatic'];

// Buffer time configuration - 15 minutes between lessons
const LESSON_BUFFER_MINUTES = 15;

interface Area {
  id: string;
  name: string;
  postcode: string;
}

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  area: string;
  status: string;
}

const PublicBookLesson: React.FC = () => {
  // Step state
  const [step, setStep] = useState(1);

  // Form state
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    courseId: '',
    courseName: '',
    carType: '',
    area: '',
    instructorId: '',
    instructorName: '',
    date: '',
    time: '',
    pickupAddress: '',
    meetingPoint: '',
    postcode: '',
    phone: '',
    paymentStatus: '', // 'pending' or 'paid'
    acceptTerms: false,
    selectedHours: 1, // New: for individual courses (supports decimals like 1.5)
    isRecurring: false, // New: for block lessons
    wantsToRegister: false, // New: registration option
    password: '', // New: password for registration
    // Recurring configuration
    recurringLessonDuration: 2, // 1 or 2 hours
    recurringLessonsPerWeek: 2, // 1, 2, or 3
    recurringPreferredDays: [] as string[], // ['Monday', 'Tuesday', ...]
    scheduleType: 'recurring' as 'recurring' | 'manual', // 'recurring' or 'manual'
    // Manual scheduling (for registered users)
    manualLessons: [] as Array<{ date: string; time: string; duration: number }>,
  });
  const [errors, setErrors] = useState<any>({});
  const [isRegistering, setIsRegistering] = useState(false);

  // Courses
  const [courses, setCourses] = useState<any[]>([]);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState('');
  const [timeOverlapError, setTimeOverlapError] = useState('');

  // Course details and pricing
  const [selectedCourse, setSelectedCourse] = useState<any>(null);
  const [coursePrice, setCoursePrice] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [showRecurringOption, setShowRecurringOption] = useState(false);

  // Areas and Instructors
  const [areas, setAreas] = useState<Area[]>([]);
  const [loadingAreas, setLoadingAreas] = useState(true);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [loadingInstructors, setLoadingInstructors] = useState(false);

  // Fetch courses on mount
  useEffect(() => {
    (async () => {
      setLoadingCourses(true);
      try {
        const data = await getAllCourses();
        setCourses(data);
      } catch (e) {
        setCourses([]);
      } finally {
        setLoadingCourses(false);
      }
    })();
  }, []);

  // Fetch areas on mount
  useEffect(() => {
    const fetchAreas = async () => {
      setLoadingAreas(true);
      try {
        const db = getFirestore();
        const areasRef = collection(db, 'areas');
        const snapshot = await getDocs(areasRef);
        const areasData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Area[];
        setAreas(areasData);
      } catch (e) {
        console.error('Error fetching areas:', e);
        setAreas([]);
      } finally {
        setLoadingAreas(false);
      }
    };
    fetchAreas();
  }, []);

  // Fetch instructors when area changes
  useEffect(() => {
    const fetchInstructors = async () => {
      if (!form.area) {
        setInstructors([]);
        return;
      }
      setLoadingInstructors(true);
      try {
        const db = getFirestore();
        const instructorsRef = collection(db, 'users');
        const q = query(
          instructorsRef,
          where('role', '==', 'instructor'),
          where('area', '==', form.area),
          where('status', '==', 'active')
        );
        const snapshot = await getDocs(q);
        const instructorsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Instructor[];
        setInstructors(instructorsData);
      } catch (e) {
        console.error('Error fetching instructors:', e);
        setInstructors([]);
      } finally {
        setLoadingInstructors(false);
      }
    };
    fetchInstructors();
  }, [form.area]);

  // Add a debug log for step changes
  useEffect(() => {
    console.log('Current step:', step);
  }, [step]);

  // Step 1 validation
  const validateStep1 = () => {
    const errs: any = {};
    if (!form.firstName) errs.firstName = 'First name is required';
    if (!form.lastName) errs.lastName = 'Last name is required';
    if (!form.email) errs.email = 'Email is required';
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = 'Invalid email';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // State for recurring availability check
  const [checkingRecurringAvailability, setCheckingRecurringAvailability] = useState(false);
  const [recurringAvailabilityError, setRecurringAvailabilityError] = useState('');

  // Check recurring availability when configuration changes
  const checkRecurringAvailability = async () => {
    if (!selectedCourse || !isBlockLesson(selectedCourse) || !form.isRecurring || form.scheduleType !== 'recurring') {
      setRecurringAvailabilityError('');
      return;
    }

    if (!form.date || !form.time || !form.instructorId || !form.recurringPreferredDays.length) {
      setRecurringAvailabilityError('');
      return;
    }

    setCheckingRecurringAvailability(true);
    setRecurringAvailabilityError('');

    try {
      const db = getFirestore();
      const selectedInstructor = instructors.find(i => i.id === form.instructorId);
      
      if (!selectedInstructor) {
        setRecurringAvailabilityError('Instructor not found');
        return;
      }

      const lessonDuration = form.recurringLessonDuration || 2;
      const lessonsPerWeek = form.recurringLessonsPerWeek || 2;
      const totalSessions = Math.ceil((selectedCourse.totalHours || 10) / lessonDuration);
      
      // Calculate all dates
      const allDates = calculateRecurringDates(
        form.date,
        form.recurringPreferredDays,
        lessonsPerWeek,
        totalSessions
      );

      // Check each date
      const conflicts: string[] = [];
      for (const date of allDates) {
        const availability = await checkDateAvailability(
          db,
          form.instructorId,
          selectedInstructor.email,
          date,
          form.time,
          lessonDuration
        );
        
        if (!availability.isAvailable) {
          conflicts.push(availability.reason || date);
        }
      }

      if (conflicts.length > 0) {
        setRecurringAvailabilityError(`Conflicts detected on ${conflicts.length} date(s). Please check the details before proceeding.`);
      } else {
        setRecurringAvailabilityError('');
      }
    } catch (error) {
      console.error('Error checking recurring availability:', error);
      setRecurringAvailabilityError('Unable to verify availability. Please try again.');
    } finally {
      setCheckingRecurringAvailability(false);
    }
  };

  // Step 2 validation
  const validateStep2 = () => {
    const errs: any = {};
    if (!form.courseId) errs.courseId = 'Please select a service/lesson';
    if (!form.carType) errs.carType = 'Please select a car type';
    if (!form.area) errs.area = 'Please select an area';
    if (!form.instructorId) errs.instructorId = 'Please select an instructor';
    if (!form.date) errs.date = 'Please select a date';
    if (!form.time) errs.time = 'Please select a time';
    
    // CRITICAL: Prevent progression if there's a time overlap error
    if (timeOverlapError) {
      errs.timeOverlap = timeOverlapError;
      console.log('❌ Step 2 validation failed due to time overlap:', timeOverlapError);
    }
    
    // CRITICAL: Prevent progression if there are recurring availability conflicts
    if (recurringAvailabilityError && !checkingRecurringAvailability) {
      errs.recurringAvailability = recurringAvailabilityError;
      console.log('❌ Step 2 validation failed due to recurring availability conflicts');
    }
    
    // Block lesson validation - must have recurring checked
    if (selectedCourse && isBlockLesson(selectedCourse)) {
      if (!form.isRecurring) {
        errs.isRecurring = 'Block lessons must be scheduled as recurring lessons. Please check the recurring option.';
      } else {
        // Validate recurring configuration
        if (form.scheduleType === 'recurring') {
          if (form.recurringPreferredDays.length < form.recurringLessonsPerWeek) {
            errs.recurringDays = `Please select at least ${form.recurringLessonsPerWeek} day(s) for ${form.recurringLessonsPerWeek} lesson(s) per week.`;
          }
        } else if (form.scheduleType === 'manual') {
          // Manual scheduling validation
          if (form.manualLessons.length === 0) {
            errs.manualLessons = 'Please add at least one lesson to your manual schedule.';
          } else {
            // Calculate total hours from manual lessons
            const totalManualHours = form.manualLessons.reduce((sum, lesson) => sum + lesson.duration, 0);
            const courseTotalHours = selectedCourse.totalHours || 10;
            if (totalManualHours !== courseTotalHours) {
              errs.manualHours = `Total hours must equal ${courseTotalHours} hours. Currently: ${totalManualHours} hours.`;
            }
          }
        }
      }
    }
    
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Step 3 validation
  const validateStep3 = () => {
    const errs: any = {};
    if (!form.pickupAddress) errs.pickupAddress = 'Address is required';
    if (!form.postcode) errs.postcode = 'Postcode is required';
    if (!form.phone) errs.phone = 'Phone number is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Step 4 validation
  const validateStep4 = () => {
    const errs: any = {};
    if (!form.acceptTerms) errs.acceptTerms = 'You must accept the terms and conditions.';
    if (form.wantsToRegister && !form.password) errs.password = 'Password is required for registration.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // User registration function with email merge strategy
  const handleUserRegistration = async (bookingData: any) => {
    setIsRegistering(true);
    
    try {
      const db = getFirestore();
      
      // Step 1: Check if email exists in users collection
      console.log('🔍 Checking for existing user with email:', bookingData.email);
      const usersRef = collection(db, 'users');
      const emailQuery = query(usersRef, where('email', '==', bookingData.email));
      const emailSnapshot = await getDocs(emailQuery);
      
      let existingUserDoc = null;
      let existingUserId = null;
      
      if (!emailSnapshot.empty) {
        existingUserDoc = emailSnapshot.docs[0];
        existingUserId = existingUserDoc.id;
        const existingUserData = existingUserDoc.data();
        console.log('📋 Found existing user:', existingUserData);
        
        // If user already has account, show error
        if (existingUserData.hasAccount === true) {
          const errorMessage = 'An account with this email already exists. Please sign in instead.';
          setErrors({ registration: errorMessage });
          throw new Error(errorMessage);
        }
        
        // If user exists without account, we'll update it
        console.log('✅ Found existing student without account, will merge with new account');
      }
      
      // Step 2: Create Firebase Auth account
      console.log('🔐 Creating Firebase Auth account...');
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        bookingData.email,
        bookingData.password
      );
      
      const user = userCredential.user;
      console.log('✅ Firebase Auth user created:', user.uid);

      // Update the user's display name
      await updateProfile(user, {
        displayName: `${bookingData.firstName} ${bookingData.lastName}`.trim()
      });

      console.log('📝 Storing/updating user data in Firestore...');
      
      // Step 3: Prepare user data
      const userData = {
        id: user.uid, // Use Firebase Auth UID as the ID
        email: bookingData.email,
        firstName: bookingData.firstName || '',
        lastName: bookingData.lastName || '',
        displayName: `${bookingData.firstName} ${bookingData.lastName}`.trim(),
        role: 'student',
        hasAccount: true, // Account created
        phone: bookingData.phone || '',
        address: bookingData.pickupAddress || '',
        meetingPoint: bookingData.meetingPoint || '',
        postcode: bookingData.postcode || '',
        gearbox: bookingData.carType || '',
        licenseNumber: '', // Not collected in public booking
        updatedAt: serverTimestamp(),
        status: 'active',
        // Additional student-specific fields
        emergencyContact: '',
        emergencyPhone: '',
        drivingExperience: 'Not specified',
        medicalConditions: false,
        medicalDetails: '',
        nervousAboutDriving: false,
        specificGoals: 'Not specified',
        focusAreas: 'Not specified',
      };

      // Step 4: If existing user without account, update it and migrate data
      if (existingUserDoc && existingUserId) {
        const existingData = existingUserDoc.data();
        
        // Merge data (keep existing data, update with new)
        const mergedData = {
          ...existingData,
          ...userData,
          createdAt: existingData.createdAt || serverTimestamp(), // Keep original createdAt
        };
        
        // Update existing document with new Firebase Auth UID
        await setDoc(doc(db, 'users', user.uid), mergedData);
        
        // Delete old document if ID is different
        if (existingUserId !== user.uid) {
          await setDoc(doc(db, 'users', existingUserId), { deleted: true, migratedTo: user.uid }, { merge: true });
        }
        
        // Update all enrollments and schedules to use new UID
        console.log('🔄 Migrating enrollments and schedules to new user ID...');
        const enrollmentsQuery = query(collection(db, 'enrollments'), where('userId', '==', existingUserId));
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        const enrollmentsBatch = enrollmentsSnapshot.docs.map(doc => 
          setDoc(doc.ref, { userId: user.uid, studentId: user.uid }, { merge: true })
        );
        
        const schedulesQuery = query(collection(db, 'schedules'), where('studentId', '==', existingUserId));
        const schedulesSnapshot = await getDocs(schedulesQuery);
        const schedulesBatch = schedulesSnapshot.docs.map(doc => 
          setDoc(doc.ref, { studentId: user.uid }, { merge: true })
        );
        
        await Promise.all([...enrollmentsBatch, ...schedulesBatch]);
        console.log('✅ Data migration completed');
      } else {
        // Create new user document
        await setDoc(doc(db, 'users', user.uid), {
          ...userData,
          createdAt: serverTimestamp(),
        });
      }
      
      console.log('✅ User data stored in Firestore');

      return user.uid;
    } catch (error: any) {
      console.error('❌ Registration error:', error);
      
      // Handle specific Firebase Auth errors
      let errorMessage = 'Registration failed. Please try again.';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'An account with this email already exists. Please sign in instead.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please use at least 6 characters.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setErrors({ registration: errorMessage });
      throw new Error(errorMessage);
    } finally {
      setIsRegistering(false);
    }
  };

  // Real-time overlap check when time is selected
  const checkTimeSlotOverlap = async (selectedTime: string) => {
    if (!selectedTime || !form.date || !form.instructorId || !selectedCourse) {
      return { hasOverlap: false, reason: '' };
    }

    try {
      const db = getFirestore();
      
      // Get all existing lessons for this instructor on this date
      const schedulesRef = collection(db, 'schedules');
      const allLessonsQuery = query(
        schedulesRef,
        where('instructorId', '==', form.instructorId),
        where('date', '==', form.date),
        where('status', 'in', ['pending', 'confirmed', 'completed'])
      );
      
      const allLessonsSnapshot = await getDocs(allLessonsQuery);
      
      // Calculate the selected lesson's time range
      const newStartMinutes = timeToMinutes(selectedTime);
      const newDuration = selectedCourse && isIndividualCourse(selectedCourse) 
        ? (form.selectedHours || 1) 
        : (selectedCourse?.totalHours || 1);
      const newEndMinutes = newStartMinutes + (newDuration * 60);
      
      console.log('🔍 Real-time overlap check for time selection:', {
        selectedTime,
        newDuration,
        newStartMinutes,
        newEndMinutes,
        totalLessonsFound: allLessonsSnapshot.docs.length
      });
      
      // Check for overlaps with existing lessons
      for (const doc of allLessonsSnapshot.docs) {
        const existingLesson = doc.data();
        
        // Skip cancelled lessons
        if (existingLesson.status === 'cancelled') {
          continue;
        }
        
        const existingStartMinutes = timeToMinutes(existingLesson.startTime);
        const existingDuration = existingLesson.duration || 1;
        const existingEndMinutes = existingStartMinutes + (existingDuration * 60);
        
        // Check for overlap
        const hasOverlap = newStartMinutes < existingEndMinutes && newEndMinutes > existingStartMinutes;
        
        if (hasOverlap) {
          const overlapStart = Math.max(newStartMinutes, existingStartMinutes);
          const overlapEnd = Math.min(newEndMinutes, existingEndMinutes);
          const overlapDuration = (overlapEnd - overlapStart) / 60;
          
          console.log('❌ Real-time overlap detected:', {
            selectedTime,
            existingLesson: existingLesson.startTime,
            overlapDuration: `${overlapDuration} hours`,
            existingStudent: existingLesson.studentName
          });
          
          return {
            hasOverlap: true,
            reason: `This time slot overlaps with an existing lesson (${existingLesson.studentName} from ${existingLesson.startTime} to ${minutesToTime(existingEndMinutes)}). Please select a different time.`
          };
        }
      }
      
      return { hasOverlap: false, reason: '' };
    } catch (error) {
      console.error('Error in real-time overlap check:', error);
      return { hasOverlap: false, reason: '' };
    }
  };

  // Helper function to calculate all recurring dates
  const calculateRecurringDates = (startDate: string, preferredDays: string[], lessonsPerWeek: number, totalSessions: number): string[] => {
    const dates: string[] = [];
    const start = new Date(startDate);
    
    // Get day indices for preferred days and sort them
    const dayIndices = preferredDays.map(day => 
      ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(day)
    ).sort((a, b) => a - b);
    
    // Find first occurrence of each preferred day starting from start date
    const firstOccurrences: Date[] = [];
    for (const dayIndex of dayIndices) {
      const currentDay = start.getDay();
      let daysToAdd = dayIndex - currentDay;
      if (daysToAdd < 0) daysToAdd += 7;
      if (daysToAdd === 0 && dayIndex === currentDay) {
        firstOccurrences.push(new Date(start));
      } else {
        const nextDate = new Date(start);
        nextDate.setDate(start.getDate() + daysToAdd);
        firstOccurrences.push(nextDate);
      }
    }
    
    // Calculate total weeks needed
    const totalWeeks = Math.ceil(totalSessions / lessonsPerWeek);
    
    // Generate all dates
    for (let week = 0; week < totalWeeks; week++) {
      for (let dayIdx = 0; dayIdx < preferredDays.length; dayIdx++) {
        if (dates.length >= totalSessions) break;
        
        const firstOccurrence = firstOccurrences[dayIdx];
        const sessionDate = new Date(firstOccurrence);
        sessionDate.setDate(firstOccurrence.getDate() + (week * 7));
        
        dates.push(sessionDate.toISOString().split('T')[0]);
      }
      if (dates.length >= totalSessions) break;
    }
    
    // Sort and limit
    dates.sort();
    if (dates.length > totalSessions) {
      dates.splice(totalSessions);
    }
    
    return dates;
  };

  // Check availability for a specific date and time
  const checkDateAvailability = async (db: any, instructorId: string, instructorEmail: string, date: string, time: string, duration: number): Promise<{ isAvailable: boolean; reason?: string }> => {
    try {
      // Check existing lessons on this date
      const schedulesRef = collection(db, 'schedules');
      const lessonsQuery = query(
        schedulesRef,
        where('instructorId', '==', instructorId),
        where('date', '==', date),
        where('status', 'in', ['pending', 'confirmed', 'completed'])
      );
      
      const lessonsSnapshot = await getDocs(lessonsQuery);
      
      const newStartMinutes = timeToMinutes(time);
      const newEndMinutes = newStartMinutes + (duration * 60);
      
      // Check for overlaps with existing lessons
      for (const doc of lessonsSnapshot.docs) {
        const existingLesson = doc.data();
        
        if (existingLesson.status === 'cancelled') {
          continue;
        }
        
        const existingStartMinutes = timeToMinutes(existingLesson.startTime);
        const existingDuration = existingLesson.duration || 1;
        const existingEndMinutes = existingStartMinutes + (existingDuration * 60);
        
        // Check for overlap (including buffer time)
        const hasOverlap = newStartMinutes < (existingEndMinutes + LESSON_BUFFER_MINUTES) && 
                          (newEndMinutes + LESSON_BUFFER_MINUTES) > existingStartMinutes;
        
        if (hasOverlap) {
          return {
            isAvailable: false,
            reason: `${date}: Time slot overlaps with existing lesson (${existingLesson.studentName} from ${existingLesson.startTime} to ${minutesToTime(existingEndMinutes)})`
          };
        }
      }
      
      // Check instructor unavailability
        const instructorAvailabilityRef = collection(db, 'instructor_availability');
        const availabilityQuery = query(
          instructorAvailabilityRef,
        where('instructorEmail', '==', instructorEmail)
        );
        const availabilitySnapshot = await getDocs(availabilityQuery);
        
        for (const doc of availabilitySnapshot.docs) {
          const data = doc.data();
          if (data.unavailableSlots) {
            for (const slot of data.unavailableSlots) {
            if (slot.date === date) {
                if (slot.isFullDay) {
                  return {
                    isAvailable: false,
                  reason: `${date}: Instructor is unavailable for the entire day`
                  };
                } else if (slot.startTime && slot.endTime) {
                  const slotStartMinutes = timeToMinutes(slot.startTime);
                  const slotEndMinutes = timeToMinutes(slot.endTime);
                const bookingStartMinutes = timeToMinutes(time);
                  
                  if (bookingStartMinutes >= slotStartMinutes && bookingStartMinutes < slotEndMinutes) {
                    return {
                      isAvailable: false,
                    reason: `${date}: Instructor is unavailable from ${slot.startTime} to ${slot.endTime}`
                    };
                  }
                }
              }
            }
          }
        }
      
      return { isAvailable: true };
    } catch (error) {
      console.error(`Error checking availability for ${date}:`, error);
      return {
        isAvailable: false,
        reason: `${date}: Unable to verify availability`
      };
    }
  };

  // Final availability check function to prevent race conditions and overlapping lessons
  const checkFinalAvailability = async (bookingData: any) => {
    try {
      const db = getFirestore();
      const selectedInstructor = instructors.find(i => i.id === bookingData.instructorId);
      
      if (!selectedInstructor) {
        return {
          isAvailable: false,
          reason: 'Instructor not found'
        };
      }

      // Check if this is a recurring block lesson
      const isBlock = isBlockLesson(selectedCourse);
      const isRecurring = bookingData.isRecurring && isBlock;
      
      if (isRecurring && bookingData.scheduleType === 'recurring') {
        // Calculate all recurring dates
        const lessonDuration = bookingData.recurringLessonDuration || 2;
        const lessonsPerWeek = bookingData.recurringLessonsPerWeek || 2;
        const preferredDays = bookingData.recurringPreferredDays || [];
        const totalSessions = Math.ceil((selectedCourse.totalHours || 10) / lessonDuration);
        
        console.log('🔍 Checking availability for all recurring dates:', {
          totalSessions,
          lessonDuration,
          lessonsPerWeek,
          preferredDays,
          startDate: bookingData.date,
          startTime: bookingData.time
        });
        
        // Calculate all dates that will be generated
        const allDates = calculateRecurringDates(
          bookingData.date,
          preferredDays,
          lessonsPerWeek,
          totalSessions
        );
        
        console.log('📅 All recurring dates to check:', allDates);
        
        // Check availability for each date
        const conflicts: string[] = [];
        for (const date of allDates) {
          const availability = await checkDateAvailability(
            db,
            bookingData.instructorId,
            selectedInstructor.email,
            date,
            bookingData.time,
            lessonDuration
          );
          
          if (!availability.isAvailable) {
            conflicts.push(availability.reason || date);
          }
        }
        
        if (conflicts.length > 0) {
          return {
            isAvailable: false,
            reason: `The following dates have conflicts:\n${conflicts.join('\n')}\n\nPlease select different days or times.`
          };
        }
        
        console.log('✅ All recurring dates are available');
      return { isAvailable: true };
      } else if (bookingData.scheduleType === 'manual' && bookingData.manualLessons) {
        // Check all manual lessons
        console.log('🔍 Checking availability for all manual lessons:', bookingData.manualLessons);
        
        const conflicts: string[] = [];
        for (const lesson of bookingData.manualLessons) {
          const availability = await checkDateAvailability(
            db,
            bookingData.instructorId,
            selectedInstructor.email,
            lesson.date,
            lesson.time,
            lesson.duration
          );
          
          if (!availability.isAvailable) {
            conflicts.push(availability.reason || `${lesson.date} at ${lesson.time}`);
          }
        }
        
        if (conflicts.length > 0) {
          return {
            isAvailable: false,
            reason: `The following lessons have conflicts:\n${conflicts.join('\n')}\n\nPlease adjust your schedule.`
          };
        }
        
        console.log('✅ All manual lessons are available');
        return { isAvailable: true };
      } else {
        // Single lesson check (original logic)
        return await checkDateAvailability(
          db,
          bookingData.instructorId,
          selectedInstructor.email,
          bookingData.date,
          bookingData.time,
          selectedCourse && isIndividualCourse(selectedCourse) 
            ? (form.selectedHours || 1) 
            : (selectedCourse?.totalHours || 1)
        );
      }
    } catch (error) {
      console.error('Error in final availability check:', error);
      return {
        isAvailable: false,
        reason: 'Unable to verify availability. Please try again.'
      };
    }
  };

  // Firestore write logic (already implemented as handlePublicBookingSubmit)
  async function handlePublicBookingSubmit(bookingData) {
    console.log('🚀 Starting handlePublicBookingSubmit with data:', bookingData);
    
    // CRITICAL: Perform final availability check before creating booking
    console.log('🔍 Performing final availability check before booking creation...');
    const finalAvailabilityCheck = await checkFinalAvailability(bookingData);
    
    if (!finalAvailabilityCheck.isAvailable) {
      console.error('❌ Final availability check failed:', finalAvailabilityCheck.reason);
      throw new Error(finalAvailabilityCheck.reason || 'Time slot is no longer available');
    }
    console.log('✅ Final availability check passed - proceeding with booking creation');
    
    const db = getFirestore();
    let finalUserId = null;
    let isRegisteredUser = false;
    let hasAccount = false;

    // Handle user registration if requested
    if (bookingData.wantsToRegister && bookingData.password) {
      try {
        console.log('📝 Creating user account...');
        finalUserId = await handleUserRegistration(bookingData);
        isRegisteredUser = true;
        hasAccount = true;
        console.log('✅ User registered successfully with ID:', finalUserId);
      } catch (error) {
        console.error('❌ Registration failed:', error);
        // Continue with public booking if registration fails - create student without account
        finalUserId = `student_${crypto.randomUUID()}`;
        isRegisteredUser = false;
        hasAccount = false;
      }
    } else {
      // Generate UUID for students without accounts
      finalUserId = `student_${crypto.randomUUID()}`;
      isRegisteredUser = false;
      hasAccount = false;
    }

    // Always create student record in users collection
    console.log('👤 Creating student record in users collection...');
    const studentData = {
      id: finalUserId,
      email: bookingData.email,
      firstName: bookingData.firstName || '',
      lastName: bookingData.lastName || '',
      displayName: `${bookingData.firstName || ''} ${bookingData.lastName || ''}`.trim(),
      role: 'student',
      hasAccount: hasAccount,
      phone: bookingData.phone || '',
      address: bookingData.pickupAddress || '',
      meetingPoint: bookingData.meetingPoint || '',
      postcode: bookingData.postcode || '',
      gearbox: bookingData.carType || '',
      licenseNumber: '',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'active',
      emergencyContact: '',
      emergencyPhone: '',
      drivingExperience: 'Not specified',
      medicalConditions: false,
      medicalDetails: '',
      nervousAboutDriving: false,
      specificGoals: 'Not specified',
      focusAreas: 'Not specified',
    };

    // Only create/update if not already created by handleUserRegistration
    if (!hasAccount) {
      await setDoc(doc(db, 'users', finalUserId), studentData);
      console.log('✅ Student record created without account');
    } else {
      console.log('✅ Student record already exists with account');
    }

    console.log('🆔 Final user ID:', finalUserId, 'Registered:', isRegisteredUser, 'Has Account:', hasAccount);

    // Format date for display
    const displayDate = bookingData.date
      ? format(new Date(bookingData.date), 'MMM d, yyyy')
      : 'To be scheduled';
    // Compose student name
    const studentName = `${bookingData.firstName || ''} ${bookingData.lastName || ''}`.trim();
    // Compose action
    const action = 'Assign Instructor';
    // Compose time
    const time = bookingData.time || 'To be scheduled';
    // Compose course type (fallback to courseName if not present)
    const courseType = bookingData.courseType || bookingData.courseName || '';
    // Compose course name
    const courseName = bookingData.courseName || '';
    
    console.log('📋 Processed booking data:', {
      studentName,
      courseName,
      courseType,
      time,
      displayDate
    });
    
    // Get selected course details
    const selectedCourse = courses.find(c => c.id === bookingData.courseId);
    console.log('📚 Selected course:', selectedCourse);
    
    // Ensure we have proper course information
    if (!selectedCourse) {
      throw new Error('Selected course not found. Please try again.');
    }
    
    // Get complete course information from the courses collection
    const courseRef = doc(db, 'courses', bookingData.courseId);
    const courseSnap = await getDoc(courseRef);
    
    if (!courseSnap.exists()) {
      throw new Error('Course data not found in database. Please try again.');
    }
    
    const courseData = courseSnap.data();
    console.log('📋 Complete course data:', courseData);
    
    const isPracticalCourse = selectedCourse && selectedCourse.type === 'practical';
    
    // Calculate hours and pricing
    const totalHours = isIndividualCourse(selectedCourse) ? bookingData.selectedHours : (selectedCourse?.totalHours || 1);
    const { totalPrice } = calculateCoursePricing(selectedCourse, totalHours);
    
    console.log('💰 Pricing calculation:', {
      totalHours,
      totalPrice,
      selectedCourseType: selectedCourse?.type,
      selectedCourseSubtype: selectedCourse?.subtype,
      isIndividual: isIndividualCourse(selectedCourse),
      selectedHours: bookingData.selectedHours
    });
    
    // Determine status based on course type and instructor assignment
    let status = 'pending'; // Changed from 'pending_assignment'
    let instructorId = null;
    let instructorName = '';
    let instructorEmail = '';
    
    if (isPracticalCourse && bookingData.instructorId) {
      status = 'confirmed'; // Changed from 'assigned'
      instructorId = bookingData.instructorId;
      instructorName = bookingData.instructorName || '';
      
      // Get instructor email from the instructors list
      const selectedInstructor = instructors.find(inst => inst.id === instructorId);
      instructorEmail = selectedInstructor?.email || '';
    }

    console.log('👨‍🏫 Instructor assignment:', {
      isPracticalCourse,
      instructorId,
      instructorName,
      instructorEmail,
      status
    });

    try {
      // 1. Write to enrollments with proper field mapping
      console.log('📝 Creating enrollment document...');
      const enrollmentRef = await addDoc(collection(db, 'enrollments'), {
        // Required fields for admin panel compatibility
        userId: finalUserId,
        studentId: finalUserId,
        studentName: studentName,
        studentEmail: bookingData.email || '',
        studentPhone: bookingData.phone || '',
        studentAddress: bookingData.pickupAddress || '',
        studentMeetingPoint: bookingData.meetingPoint || '',
        studentPostcode: bookingData.postcode || '',
        studentGearbox: bookingData.carType || '',
        studentLicenseNumber: '', // Not collected in public booking
        studentEmergencyContact: '', // Not collected in public booking
        studentEmergencyPhone: '', // Not collected in public booking
        
        // Course information - Use complete course data
        courseId: bookingData.courseId || '',
        courseName: courseData.title || selectedCourse.title || 'Unknown Course',
        courseType: courseData.type || selectedCourse.type || 'practical',
        courseSubtype: courseData.subtype || selectedCourse.subtype || '',
        
        // Instructor information
        instructorId: instructorId,
        instructorName: instructorName,
        instructorEmail: instructorEmail, // Set instructor email if instructor is assigned
        
        // Status and dates
        status: status,
        enrollmentStatus: status === 'confirmed' ? 'active' : 'pending',
        paymentStatus: bookingData.paymentStatus, // 'paid' or 'pending'
        
        // Additional fields for admin panel
        startDate: bookingData.date || '',
        preferredTime: bookingData.time || '',
        additionalNotes: `${isRegisteredUser ? 'Registered user booking' : 'Public booking'} - Area: ${bookingData.area || ''}`,
        drivingExperience: 'Not specified',
        totalHours: totalHours,
        hoursTaken: 0,
        hoursRemaining: totalHours,
        amount: totalPrice,
        currency: 'GBP',
        vehicleType: bookingData.carType || '',
        licenseType: 'Not specified',
        previousLessons: '0',
        medicalConditions: false,
        medicalDetails: '',
        nervousAboutDriving: false,
        specificGoals: 'Not specified',
        focusAreas: 'Not specified',
        isPartialPayment: false,
        amountPaid: 0,
        fullCoursePrice: totalPrice,
        remainingHours: totalHours,
        
        // Registration status
        isRegisteredUser: isRegisteredUser,
        hasAccount: hasAccount,
        
        // Timestamps
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        enrolledAt: serverTimestamp(),
        
        // Public booking specific fields (preserved for reference)
        displayDate: displayDate,
        time: time,
        action: action,
        area: bookingData.area || '',
        pickupAddress: bookingData.pickupAddress || '',
        meetingPoint: bookingData.meetingPoint || '',
        postcode: bookingData.postcode || '',
        carType: bookingData.carType || '',
        acceptTerms: bookingData.acceptTerms || false,
        selectedHours: bookingData.selectedHours || 1,
        isRecurring: bookingData.isRecurring || false,
        
        // Recurring configuration
        recurringConfig: bookingData.isRecurring && bookingData.scheduleType === 'recurring' ? {
          lessonDuration: bookingData.recurringLessonDuration || 2,
          lessonsPerWeek: bookingData.recurringLessonsPerWeek || 2,
          preferredDays: bookingData.recurringPreferredDays || [],
          startDate: bookingData.date || '',
          startTime: bookingData.time || ''
        } : null,
        
        // Manual schedule
        manualSchedule: bookingData.scheduleType === 'manual' && bookingData.manualLessons ? bookingData.manualLessons : null,
        scheduleType: bookingData.scheduleType || 'recurring',
        
        // Save all original fields for reference
        ...bookingData,
      });
      
      console.log('✅ Enrollment created successfully with ID:', enrollmentRef.id);

      // 2. Create schedules based on course type and recurring settings
      console.log('📅 Starting schedule creation...');
      console.log('🔍 Schedule creation conditions:', {
        isRecurring: bookingData.isRecurring,
        isBlockLesson: isBlockLesson(selectedCourse),
        scheduleType: bookingData.scheduleType,
        selectedCourse: selectedCourse?.title
      });
      
      // Manual scheduling (for registered users)
      if (bookingData.scheduleType === 'manual' && bookingData.manualLessons && bookingData.manualLessons.length > 0) {
        console.log('📅 Creating manual schedules...');
        
        for (let i = 0; i < bookingData.manualLessons.length; i++) {
          const lesson = bookingData.manualLessons[i];
          const startTime = lesson.time || '';
          const startMinutes = timeToMinutes(startTime);
          const endMinutes = startMinutes + (lesson.duration * 60);
          const endTime = minutesToTime(endMinutes);
          
          console.log(`📅 Creating manual lesson ${i + 1}/${bookingData.manualLessons.length}:`, {
            date: lesson.date,
            time: lesson.time,
            duration: lesson.duration
          });
          
          const scheduleData = {
            courseName: courseData.title || selectedCourse.title || 'Unknown Course',
            courseSubtype: courseData.subtype || selectedCourse.subtype || '',
            courseType: courseData.type || selectedCourse.type || 'practical',
            date: lesson.date,
            startTime: startTime,
            endTime: endTime,
            startMinutes: startMinutes,
            endMinutes: endMinutes,
            duration: lesson.duration,
            studentId: finalUserId,
            studentName: studentName,
            studentEmail: bookingData.email || '',
            studentPhone: bookingData.phone || '',
            studentAddress: bookingData.pickupAddress || '',
            studentMeetingPoint: bookingData.meetingPoint || '',
            studentPostcode: bookingData.postcode || '',
            studentGearbox: bookingData.carType || '',
            instructorId: instructorId,
            instructorName: instructorName,
            instructorEmail: instructorEmail,
            enrollmentId: enrollmentRef.id,
            status: status,
            hoursTaken: 0,
            hoursRemaining: totalHours,
            totalHours: totalHours,
            licenseType: 'Not specified',
            vehicleType: bookingData.carType || 'Not specified',
            venue: '',
            notes: '',
            instructorNotified: false,
            isRecurring: false,
            isManualSchedule: true,
            sessionNumber: i + 1,
            totalSessions: bookingData.manualLessons.length,
            isRegisteredUser: isRegisteredUser,
            hasAccount: hasAccount,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          
          const scheduleRef = await addDoc(collection(db, 'schedules'), scheduleData);
          console.log(`✅ Manual lesson ${i + 1} created with ID:`, scheduleRef.id);
        }
      } else if (bookingData.isRecurring && isBlockLesson(selectedCourse)) {
        console.log('🔄 Creating recurring schedules for block lesson...');
        
        // Get recurring configuration
        const lessonDuration = bookingData.recurringLessonDuration || 2;
        const lessonsPerWeek = bookingData.recurringLessonsPerWeek || 2;
        const preferredDays = bookingData.recurringPreferredDays || [];
        const totalSessions = Math.ceil((selectedCourse.totalHours || 10) / lessonDuration);
        const totalWeeks = Math.ceil(totalSessions / lessonsPerWeek);
        
        console.log('📊 Recurring schedule details:', {
          totalSessions,
          lessonDuration,
          lessonsPerWeek,
          preferredDays,
          totalWeeks,
          totalHours: selectedCourse.totalHours
        });
        
        // Helper function to get day of week name
        const getDayName = (date: Date): string => {
          const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          return days[date.getDay()];
        };
        
        // Helper function to get next occurrence of a day
        const getNextDay = (startDate: Date, targetDay: string): Date => {
          const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          const targetDayIndex = days.indexOf(targetDay);
          const currentDayIndex = startDate.getDay();
          
          let daysToAdd = targetDayIndex - currentDayIndex;
          if (daysToAdd <= 0) {
            daysToAdd += 7; // Next week
          }
          
          const nextDate = new Date(startDate);
          nextDate.setDate(startDate.getDate() + daysToAdd);
          return nextDate;
        };
        
        // Generate schedule using preferred days
        const startDate = new Date(bookingData.date);
          const startTime = bookingData.time || '';
        const scheduledDates: Date[] = [];
        
        // Get day indices for preferred days and sort them
        const dayIndices = preferredDays.map(day => 
          ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(day)
        ).sort((a, b) => a - b);
        
        // Find first occurrence of each preferred day starting from start date
        const firstOccurrences: Date[] = [];
        for (const dayIndex of dayIndices) {
          const currentDay = startDate.getDay();
          let daysToAdd = dayIndex - currentDay;
          if (daysToAdd < 0) daysToAdd += 7;
          if (daysToAdd === 0 && dayIndex === currentDay) {
            // Start date is on this day
            firstOccurrences.push(new Date(startDate));
          } else {
            const nextDate = new Date(startDate);
            nextDate.setDate(startDate.getDate() + daysToAdd);
            firstOccurrences.push(nextDate);
          }
        }
        
        // Generate all sessions across all weeks
        for (let week = 0; week < totalWeeks; week++) {
          for (let dayIdx = 0; dayIdx < preferredDays.length; dayIdx++) {
            if (scheduledDates.length >= totalSessions) break;
            
            const firstOccurrence = firstOccurrences[dayIdx];
            const sessionDate = new Date(firstOccurrence);
            sessionDate.setDate(firstOccurrence.getDate() + (week * 7));
            
            scheduledDates.push(sessionDate);
          }
          if (scheduledDates.length >= totalSessions) break;
        }
        
        // Sort dates chronologically and limit to total sessions
        scheduledDates.sort((a, b) => a.getTime() - b.getTime());
        if (scheduledDates.length > totalSessions) {
          scheduledDates.splice(totalSessions);
        }
        
        // Create schedule entries
        for (let i = 0; i < scheduledDates.length; i++) {
          const sessionDate = scheduledDates[i];
          const sessionDateStr = sessionDate.toISOString().split('T')[0];
          const startMinutes = timeToMinutes(startTime);
          const endMinutes = startMinutes + (lessonDuration * 60);
          const endTime = minutesToTime(endMinutes);
          
          console.log(`📅 Creating session ${i + 1}/${totalSessions}:`, {
            sessionDate: sessionDateStr,
            startTime,
            endTime,
            duration: lessonDuration
          });
          
          const scheduleData = {
            courseName: courseData.title || selectedCourse.title || 'Unknown Course',
            courseSubtype: courseData.subtype || selectedCourse.subtype || '',
            courseType: courseData.type || selectedCourse.type || 'practical',
            date: sessionDateStr,
            startTime: startTime,
            endTime: endTime,
            startMinutes: startMinutes,
            endMinutes: endMinutes,
            duration: lessonDuration,
            studentId: finalUserId,
            studentName: studentName,
            studentEmail: bookingData.email || '',
            studentPhone: bookingData.phone || '',
            studentAddress: bookingData.pickupAddress || '',
            studentMeetingPoint: bookingData.meetingPoint || '',
            studentPostcode: bookingData.postcode || '',
            studentGearbox: bookingData.carType || '',
            instructorId: instructorId,
            instructorName: instructorName,
            instructorEmail: instructorEmail,
            enrollmentId: enrollmentRef.id,
            status: status,
            hoursTaken: 0,
            hoursRemaining: totalHours,
            totalHours: totalHours,
            licenseType: 'Not specified',
            vehicleType: bookingData.carType || 'Not specified',
            venue: '',
            notes: '',
            instructorNotified: false,
            sessionNumber: i + 1,
            totalSessions: totalSessions,
            isRecurring: true,
            recurringConfig: {
              lessonDuration,
              lessonsPerWeek,
              preferredDays,
              startDate: bookingData.date,
              startTime: bookingData.time
            },
            isRegisteredUser: isRegisteredUser,
            hasAccount: hasAccount,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          
          const scheduleRef = await addDoc(collection(db, 'schedules'), scheduleData);
          console.log(`✅ Schedule ${i + 1} created with ID:`, scheduleRef.id);
        }
      } else {
        console.log('📅 Creating single schedule for regular booking...');
        // Create single schedule for regular bookings
        // Calculate duration based on course type
        let lessonDuration = 1; // default
        
        if (isIndividualCourse(selectedCourse)) {
          lessonDuration = bookingData.selectedHours || 1;
        } else if (selectedCourse?.title?.toLowerCase().includes('car hire')) {
          lessonDuration = selectedCourse?.totalHours || 3; // Car hire is usually 3 hours
        } else {
          lessonDuration = selectedCourse?.totalHours || 1;
        }
        
        const startTime = bookingData.time || '';
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = startMinutes + (lessonDuration * 60);
        const endTime = minutesToTime(endMinutes);
        
        console.log('📊 Single schedule details:', {
          lessonDuration,
          startTime,
          endTime,
          startMinutes,
          endMinutes,
          courseTitle: selectedCourse?.title,
          courseType: selectedCourse?.type,
          totalHours: selectedCourse?.totalHours,
          isDecimalDuration: lessonDuration % 1 !== 0,
          durationInMinutes: lessonDuration * 60
        });
        
        const scheduleData = {
          // Course information
          courseName: courseData.title || selectedCourse.title || 'Unknown Course',
          courseSubtype: courseData.subtype || selectedCourse.subtype || '',
          courseType: courseData.type || selectedCourse.type || 'practical',
          
          // Date and time information
          date: bookingData.date || '',
          startTime: startTime,
          endTime: endTime,
          startMinutes: startMinutes,
          endMinutes: endMinutes,
          duration: lessonDuration,
          
          // Student information
          studentId: finalUserId,
          studentName: studentName,
          studentEmail: bookingData.email || '',
          studentPhone: bookingData.phone || '',
          studentAddress: bookingData.pickupAddress || '',
          studentMeetingPoint: bookingData.meetingPoint || '',
          studentPostcode: bookingData.postcode || '',
          studentGearbox: bookingData.carType || '',
          
          // Instructor information
          instructorId: instructorId,
          instructorName: instructorName,
          instructorEmail: instructorEmail, // Set instructor email if instructor is assigned
          
          // Enrollment information
          enrollmentId: enrollmentRef.id,
          
          // Status and progress
          status: status,
          hoursTaken: 0,
          hoursRemaining: totalHours,
          totalHours: totalHours,
          
          // Additional fields
          licenseType: 'Not specified',
          vehicleType: bookingData.carType || 'Not specified',
          venue: '',
          notes: '',
          instructorNotified: false,
          
          // Registration status
          isRegisteredUser: isRegisteredUser,
          hasAccount: hasAccount,
          
          // Timestamps
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        
        console.log('📝 Single schedule data to be created:', scheduleData);
        
        const scheduleRef = await addDoc(collection(db, 'schedules'), scheduleData);
        console.log('✅ Single schedule created with ID:', scheduleRef.id);
      }
      
      console.log('🎉 All schedules created successfully!');
      
      // 3. Create payment record for registered users
      if (isRegisteredUser) {
        console.log('💳 Creating payment record for registered user...');
        
        const paymentData = {
          userId: finalUserId,
          studentId: finalUserId,
          studentName: studentName,
          studentEmail: bookingData.email || '',
          courseId: bookingData.courseId || '',
          courseName: courseData.title || selectedCourse.title || 'Unknown Course',
          enrollmentId: enrollmentRef.id,
          amount: totalPrice,
          currency: 'GBP',
          paymentMethod: bookingData.paymentStatus === 'paid' ? 'card' : 'cash',
          paymentType: 'course_enrollment',
          status: bookingData.paymentStatus || 'pending',
          description: `Payment for ${courseData.title || selectedCourse.title} course`,
          instructorId: instructorId || null,
          instructorName: instructorName || '',
          instructorEmail: instructorEmail || '',
          hours: totalHours,
          hoursRemaining: totalHours,
          hoursTaken: 0,
          lessonType: courseData.type || selectedCourse.type || 'practical',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          paidAt: bookingData.paymentStatus === 'paid' ? serverTimestamp() : null,
          paymentId: `public_booking_${Date.now()}`,
          isPublicBooking: true,
          bookingDate: bookingData.date || '',
          bookingTime: bookingData.time || '',
          area: bookingData.area || '',
          carType: bookingData.carType || '',
        };
        
        const paymentRef = await addDoc(collection(db, 'studentPayments'), paymentData);
        console.log('✅ Payment record created with ID:', paymentRef.id);
      }
      
    } catch (error) {
      console.error('❌ Error in handlePublicBookingSubmit:', error);
      throw error;
    }
  }

  // Helper function to add minutes to time
  const addMinutesToTime = (time: string, minutesToAdd: number): string => {
    const [hours, minutes] = time.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + minutesToAdd;
    const newHours = Math.floor(totalMinutes / 60);
    const newMinutes = totalMinutes % 60;
    return `${newHours.toString().padStart(2, '0')}:${newMinutes.toString().padStart(2, '0')}`;
  };

  // Helper function to convert time string to minutes
  const timeToMinutes = (time: string): number => {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Helper function to convert minutes to time string
  const minutesToTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  // Helper function to calculate course pricing
  const calculateCoursePricing = (course: any, hours: number = 1) => {
    if (!course) return { basePrice: 0, totalPrice: 0 };
    
    let basePrice = course.price || 0;
    let totalPrice = basePrice;
    
    // For individual courses, calculate based on hours
    // Check both type='individual' and subtype='individual' for compatibility
    const isIndividualCourse = course.type === 'individual' || course.subtype === 'individual';
    if (isIndividualCourse && course.pricePerHour) {
      basePrice = course.pricePerHour;
      totalPrice = basePrice * hours;
    }
    
    return { basePrice, totalPrice };
  };

  // Helper function to check if course is a block lesson
  const isBlockLesson = (course: any) => {
    console.log('🔍 Checking if course is block lesson:', {
      courseTitle: course?.title,
      courseTotalHours: course?.totalHours,
      course: course
    });
    
    const isBlock = course && (
      course.title?.toLowerCase().includes('block') ||
      course.title?.toLowerCase().includes('package') ||
      (course.totalHours && course.totalHours >= 10)
    );
    
    console.log('✅ isBlockLesson result:', isBlock);
    return isBlock;
  };

  // Helper function to check if course is individual
  const isIndividualCourse = (course: any) => {
    if (!course) return false;
    // Check both type='individual' and subtype='individual' for compatibility
    return course.type === 'individual' || course.subtype === 'individual';
  };

  // Helper function to handle course selection
  const handleCourseSelection = (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    setSelectedCourse(course);
    
    if (course) {
      const isBlock = isBlockLesson(course);
      const isIndividual = isIndividualCourse(course);
      
      setShowRecurringOption(isBlock);
      
      console.log('🔍 Course selection details:', {
        courseTitle: course.title,
        courseType: course.type,
        courseSubtype: course.subtype,
        isIndividual,
        isBlock,
        pricePerHour: course.pricePerHour,
        price: course.price
      });
      
      // Reset hours for individual courses
      if (isIndividual) {
        setForm(f => ({ 
          ...f, 
          selectedHours: 1, 
          isRecurring: false,
          scheduleType: 'recurring',
          manualLessons: []
        }));
      } else if (isBlock) {
        // For block lessons, require recurring and set defaults
        setForm(f => ({ 
          ...f, 
          selectedHours: course.totalHours || 1, 
          isRecurring: true, // Required for block lessons
          scheduleType: 'recurring',
          recurringLessonDuration: 2,
          recurringLessonsPerWeek: 2,
          recurringPreferredDays: [],
          manualLessons: []
        }));
      } else {
        setForm(f => ({ 
          ...f, 
          selectedHours: course.totalHours || 1, 
          isRecurring: false,
          scheduleType: 'recurring',
          manualLessons: []
        }));
      }
      
      // Calculate pricing
      const hours = isIndividual ? 1 : (course.totalHours || 1);
      const { basePrice, totalPrice } = calculateCoursePricing(course, hours);
      setCoursePrice(basePrice);
      setTotalPrice(totalPrice);
      
      console.log('💰 Pricing calculated:', {
        hours,
        basePrice,
        totalPrice,
        pricePerHour: course.pricePerHour
      });
    }
  };

  // Helper function to handle hour selection for individual courses
  const handleHourSelection = (hours: number) => {
    setForm(f => ({ ...f, selectedHours: hours }));
    
    if (selectedCourse && isIndividualCourse(selectedCourse)) {
      const { totalPrice } = calculateCoursePricing(selectedCourse, hours);
      setTotalPrice(totalPrice);
      
      console.log('⏰ Hour selection updated:', {
        selectedHours: hours,
        totalPrice,
        pricePerHour: selectedCourse.pricePerHour,
        durationInMinutes: hours * 60,
        isDecimal: hours % 1 !== 0
      });
    }
  };

  // Fetch available times when date, course, area, or instructor changes
  useEffect(() => {
    const fetchAvailableTimes = async () => {
      if (!form.date || !form.courseId || !form.area || !form.instructorId) {
        setAvailableTimes([]);
        return;
      }
      setCheckingAvailability(true);
      setAvailabilityError('');
      try {
        const db = getFirestore();
        const bookedTimes = new Set<string>();
        const unavailableTimes = new Set<string>();

        // 1. Check schedules for existing bookings on the selected date FOR THE SELECTED INSTRUCTOR
        console.log('🔍 Checking availability for instructor:', form.instructorId, 'on date:', form.date);
        const schedulesRef = collection(db, 'schedules');
        const schedulesQuery = query(
          schedulesRef,
          where('instructorId', '==', form.instructorId),
          where('date', '==', form.date),
          where('status', 'in', ['pending', 'confirmed', 'completed'])
        );
        const schedulesSnapshot = await getDocs(schedulesQuery);
        console.log('📅 Found', schedulesSnapshot.docs.length, 'existing bookings for this instructor on this date');
        
        schedulesSnapshot.forEach(doc => {
          const data = doc.data();
          
          // CRITICAL: Skip cancelled lessons - they should not block new bookings
          if (data.status === 'cancelled') {
            console.log('⏭️ Skipping cancelled lesson:', {
              startTime: data.startTime,
              studentName: data.studentName,
              status: data.status,
              cancelledAt: data.cancelledAt
            });
            return; // Skip this cancelled lesson
          }
          
          // Add all times in the booking slot as unavailable PLUS buffer time
          const startTime = data.startTime;
          const endTime = data.endTime || data.time;
          
          console.log('🚫 Found active booking (blocking time slots):', {
            startTime,
            endTime,
            studentName: data.studentName,
            status: data.status,
            duration: data.duration
          });
          
          if (startTime && endTime) {
            const startMinutes = timeToMinutes(startTime);
            const endMinutes = timeToMinutes(endTime);
            
            // Use duration from schedule if available, otherwise calculate from start/end times
            const duration = data.duration || Math.ceil((endMinutes - startMinutes) / 60);
            const actualEndMinutes = startMinutes + (duration * 60);
            
            // Block lesson time + 15-minute buffer for instructor break
            for (let minutes = startMinutes; minutes < actualEndMinutes + LESSON_BUFFER_MINUTES; minutes += 15) {
              const timeSlot = minutesToTime(minutes);
              bookedTimes.add(timeSlot);
              console.log('🚫 Blocking time slot:', timeSlot, `(lesson: ${startTime}-${endTime}, duration: ${duration}h)`);
            }
          }
        });

        // 2. Check instructor availability for the selected instructor
        const selectedInstructor = instructors.find(i => i.id === form.instructorId);
        if (selectedInstructor) {
          const instructorAvailabilityRef = collection(db, 'instructor_availability');
          const availabilityQuery = query(
            instructorAvailabilityRef,
            where('instructorEmail', '==', selectedInstructor.email)
          );
          const availabilitySnapshot = await getDocs(availabilityQuery);
          
          availabilitySnapshot.forEach(doc => {
            const data = doc.data();
            if (data.unavailableSlots) {
              data.unavailableSlots.forEach((slot: any) => {
                if (slot.date === form.date) {
                  if (slot.isFullDay) {
                    // Mark all day as unavailable
                    for (let h = 8; h <= 20; h++) {
                      for (let m = 0; m < 60; m += 15) {
                        const timeSlot = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                        unavailableTimes.add(timeSlot);
                      }
                    }
                  } else if (slot.startTime && slot.endTime) {
                    // Mark specific time range as unavailable
                    const startMinutes = timeToMinutes(slot.startTime);
                    const endMinutes = timeToMinutes(slot.endTime);
                    
                    for (let minutes = startMinutes; minutes < endMinutes; minutes += 15) {
                      const timeSlot = minutesToTime(minutes);
                      unavailableTimes.add(timeSlot);
                    }
                  }
                }
              });
            }
          });
        }

        // 3. Generate available time slots (8:00 AM to 8:00 PM in 15-minute intervals)
        const times: string[] = [];
        const allTimeSlots: string[] = [];
        
        for (let h = 8; h <= 20; h++) {
          for (let m = 0; m < 60; m += 15) {
            const timeSlot = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
            allTimeSlots.push(timeSlot);
            
            // Skip if time is booked or instructor is unavailable
            if (!bookedTimes.has(timeSlot) && !unavailableTimes.has(timeSlot)) {
              times.push(timeSlot);
            } else {
              const reason = bookedTimes.has(timeSlot) ? 'booked' : 'instructor unavailable';
              console.log(`🚫 Time slot ${timeSlot} is ${reason}`);
            }
          }
        }

        console.log('📊 Public Booking - Availability Summary:', {
          totalTimeSlots: allTimeSlots.length,
          availableSlots: times.length,
          bookedSlots: bookedTimes.size,
          unavailableSlots: unavailableTimes.size,
          availableTimes: times,
          lessonsChecked: schedulesSnapshot.docs.length,
          lessonsExcluded: schedulesSnapshot.docs.filter(doc => doc.data().status === 'cancelled').length,
          activeLessons: schedulesSnapshot.docs.filter(doc => doc.data().status !== 'cancelled').length
        });

        setAvailableTimes(times);
        if (times.length === 0) {
          setAvailabilityError('No available times for this date and instructor.');
        } else {
          setAvailabilityError(''); // Clear any previous errors
        }
      } catch (e) {
        console.error('Error checking availability:', e);
        setAvailableTimes([]);
        setAvailabilityError('Error checking availability.');
      } finally {
        setCheckingAvailability(false);
      }
    };
    fetchAvailableTimes();
  }, [form.date, form.courseId, form.area, form.instructorId, instructors]);

  // Check recurring availability when configuration changes
  useEffect(() => {
    if (selectedCourse && isBlockLesson(selectedCourse) && form.isRecurring && form.scheduleType === 'recurring') {
      // Debounce the check to avoid too many calls
      const timeoutId = setTimeout(() => {
        if (form.date && form.time && form.instructorId && form.recurringPreferredDays.length >= form.recurringLessonsPerWeek) {
          checkRecurringAvailability();
        } else {
          setRecurringAvailabilityError('');
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    } else {
      setRecurringAvailabilityError('');
    }
  }, [form.date, form.time, form.instructorId, form.recurringLessonDuration, form.recurringLessonsPerWeek, form.recurringPreferredDays, form.isRecurring, form.scheduleType, selectedCourse]);

  // Step 1 UI
  const renderStep1 = () => (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (validateStep1()) setStep(2);
      }}
      className="space-y-6"
    >
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
        <input
          type="text"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
          value={form.firstName}
          onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))}
        />
        {errors.firstName && <div className="text-red-500 text-xs mt-1">{errors.firstName}</div>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
        <input
          type="text"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
          value={form.lastName}
          onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))}
        />
        {errors.lastName && <div className="text-red-500 text-xs mt-1">{errors.lastName}</div>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input
          type="email"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
          value={form.email}
          onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
        />
        {errors.email && <div className="text-red-500 text-xs mt-1">{errors.email}</div>}
      </div>
      <button
        type="submit"
        className="w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-drivewise-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-700 hover:to-drivewise-700 transition"
      >
        Next
      </button>
    </form>
  );

  // Step 2 UI
  const renderStep2 = () => (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (validateStep2()) setStep(3);
      }}
      className="space-y-6"
    >
      <div className="text-lg font-semibold mb-2 text-drivewise-700">
        Step 2 of 3: What service would you like?
      </div>
      <div className="mb-2 text-gray-600">
        Thanks <span className="font-bold text-drivewise-700">{form.firstName} {form.lastName}</span> for expressing interest in our services. How can we help you?
      </div>
      
      {/* Course Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Service / Lesson</label>
        <select
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.courseId ? 'border-red-500' : 'border-gray-300'}`}
          value={form.courseId}
          onChange={e => {
            const course = courses.find(c => c.id === e.target.value);
            setForm(f => ({ ...f, courseId: e.target.value, courseName: course ? course.title : '' }));
            handleCourseSelection(e.target.value);
          }}
          disabled={loadingCourses}
        >
          <option value="">{loadingCourses ? 'Loading...' : 'Select a course'}</option>
          {courses.map(course => (
            <option key={course.id} value={course.id}>{course.title}</option>
          ))}
        </select>
        {errors.courseId && <div className="text-red-500 text-xs mt-1">{errors.courseId}</div>}
      </div>

      {/* Course Details and Pricing */}
      {selectedCourse && (
        <div className="bg-gray-50 p-4 rounded-lg space-y-3">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-gray-900">{selectedCourse.title}</h3>
              <p className="text-sm text-gray-600">{selectedCourse.description}</p>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold text-drivewise-700">
                £{totalPrice.toFixed(2)}
              </div>
              {isIndividualCourse(selectedCourse) && (
                <div className="text-xs text-gray-500">
                  £{coursePrice.toFixed(2)} per hour
                </div>
              )}
            </div>
          </div>
          
          {/* Course Information */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Course Type:</span> {selectedCourse.type}
            </div>
            <div>
              <span className="font-medium">Total Hours:</span> {selectedCourse.totalHours || form.selectedHours} hours
            </div>
            {selectedCourse.duration && (
              <div>
                <span className="font-medium">Duration:</span> {selectedCourse.duration} {selectedCourse.type === 'theory' ? 'days' : 'hours'}
              </div>
            )}
          </div>

          {/* Hour Selection for Individual Courses */}
          {selectedCourse && isIndividualCourse(selectedCourse) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Number of Hours</label>
              <div className="flex gap-2 flex-wrap">
                {[1, 1.5, 2, 3, 4, 5, 6, 7, 8].map(hours => (
                  <button
                    key={hours}
                    type="button"
                    onClick={() => handleHourSelection(hours)}
                    className={`px-4 py-2 rounded-lg border ${
                      form.selectedHours === hours
                        ? 'bg-drivewise-700 text-white border-drivewise-700'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-drivewise-500'
                    }`}
                  >
                    {hours === 1.5 ? '1.5 Hours' : `${hours} Hour${hours > 1 ? 's' : ''}`}
                  </button>
                ))}
              </div>
              {selectedCourse.pricePerHour ? (
                <div className="text-sm text-gray-600 mt-1">
                  Rate: £{selectedCourse.pricePerHour.toFixed(2)}/hour | Total: £{totalPrice.toFixed(2)} for {form.selectedHours === 1.5 ? '1.5 hours' : `${form.selectedHours} hour${form.selectedHours > 1 ? 's' : ''}`}
                </div>
              ) : (
                <div className="text-sm text-red-500 mt-1">
                  ⚠️ Price per hour not set for this course. Please contact admin.
                </div>
              )}
            </div>
          )}

          {/* Recurring Option for Block Lessons */}
          {showRecurringOption && (
            <div className="border-t pt-3 space-y-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="recurring"
                  checked={form.isRecurring}
                  onChange={e => {
                    if (!e.target.checked && isBlockLesson(selectedCourse)) {
                      // Cannot uncheck for block lessons
                      return;
                    }
                    setForm(f => ({ ...f, isRecurring: e.target.checked }));
                  }}
                  disabled={isBlockLesson(selectedCourse)}
                  className="rounded border-gray-300 text-drivewise-700 focus:ring-drivewise-500"
                />
                <label htmlFor="recurring" className="text-sm font-medium text-gray-700">
                  Book as recurring lessons {isBlockLesson(selectedCourse) && '(Required for block lessons)'}
                </label>
              </div>
              {form.isRecurring && (
                <div className="pl-6 space-y-4 border-l-2 border-drivewise-200">
                  {/* Schedule Type Selection (only for registered users) */}
                  {form.wantsToRegister && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Schedule Type</label>
                      <div className="flex gap-4">
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="scheduleType"
                            value="recurring"
                            checked={form.scheduleType === 'recurring'}
                            onChange={e => setForm(f => ({ ...f, scheduleType: 'recurring' as 'recurring' | 'manual' }))}
                            className="text-drivewise-700 focus:ring-drivewise-500"
                          />
                          <span className="text-sm">Recurring Schedule</span>
                        </label>
                        <label className="flex items-center space-x-2">
                          <input
                            type="radio"
                            name="scheduleType"
                            value="manual"
                            checked={form.scheduleType === 'manual'}
                            onChange={e => setForm(f => ({ ...f, scheduleType: 'manual' as 'recurring' | 'manual' }))}
                            className="text-drivewise-700 focus:ring-drivewise-500"
                          />
                          <span className="text-sm">Manual Schedule (Choose your own dates/times)</span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Recurring Configuration */}
                  {form.scheduleType === 'recurring' && (
                    <div className="space-y-4">
                      {/* Lesson Duration */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Lesson Duration</label>
                        <div className="flex gap-4">
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="recurringDuration"
                              value="1"
                              checked={form.recurringLessonDuration === 1}
                              onChange={e => setForm(f => ({ ...f, recurringLessonDuration: 1 }))}
                              className="text-drivewise-700 focus:ring-drivewise-500"
                            />
                            <span className="text-sm">1 hour</span>
                          </label>
                          <label className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name="recurringDuration"
                              value="2"
                              checked={form.recurringLessonDuration === 2}
                              onChange={e => setForm(f => ({ ...f, recurringLessonDuration: 2 }))}
                              className="text-drivewise-700 focus:ring-drivewise-500"
                            />
                            <span className="text-sm">2 hours</span>
                          </label>
                        </div>
                      </div>

                      {/* Lessons Per Week */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Lessons Per Week</label>
                        <div className="flex gap-4">
                          {[1, 2, 3].map(num => (
                            <label key={num} className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="lessonsPerWeek"
                                value={num.toString()}
                                checked={form.recurringLessonsPerWeek === num}
                                onChange={e => setForm(f => ({ ...f, recurringLessonsPerWeek: num }))}
                                className="text-drivewise-700 focus:ring-drivewise-500"
                              />
                              <span className="text-sm">{num} lesson{num > 1 ? 's' : ''}/week</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Preferred Days */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Preferred Days <span className="text-red-500">*</span>
                          <span className="text-xs text-gray-500 ml-2">
                            (Select at least {form.recurringLessonsPerWeek} day{form.recurringLessonsPerWeek > 1 ? 's' : ''})
                          </span>
                        </label>
                        <div className="grid grid-cols-4 gap-2">
                          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                            <label key={day} className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={form.recurringPreferredDays.includes(day)}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setForm(f => ({ ...f, recurringPreferredDays: [...f.recurringPreferredDays, day] }));
                                  } else {
                                    setForm(f => ({ ...f, recurringPreferredDays: f.recurringPreferredDays.filter(d => d !== day) }));
                                  }
                                }}
                                className="rounded border-gray-300 text-drivewise-700 focus:ring-drivewise-500"
                              />
                              <span className="text-sm">{day.substring(0, 3)}</span>
                            </label>
                          ))}
                        </div>
                        {errors.recurringDays && (
                          <div className="text-red-500 text-xs mt-1">{errors.recurringDays}</div>
                        )}
                        {form.recurringPreferredDays.length > 0 && (
                          <div className="mt-2 space-y-1">
                            <p className="text-xs text-gray-500">
                              Total sessions: {Math.ceil((selectedCourse.totalHours || 10) / form.recurringLessonDuration)} sessions over {Math.ceil(Math.ceil((selectedCourse.totalHours || 10) / form.recurringLessonDuration) / form.recurringLessonsPerWeek)} weeks
                            </p>
                            {checkingRecurringAvailability && (
                              <p className="text-xs text-blue-600">🔍 Checking availability for all recurring dates...</p>
                            )}
                            {recurringAvailabilityError && !checkingRecurringAvailability && (
                              <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
                                ⚠️ {recurringAvailabilityError}
                              </div>
                            )}
                            {!checkingRecurringAvailability && !recurringAvailabilityError && form.date && form.time && form.instructorId && form.recurringPreferredDays.length >= form.recurringLessonsPerWeek && (
                              <p className="text-xs text-green-600">✅ All recurring dates are available</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Manual Scheduling (only for registered users) */}
                  {form.scheduleType === 'manual' && form.wantsToRegister && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <p className="text-sm text-blue-800">
                          Add individual lessons. Total hours must equal <strong>{selectedCourse.totalHours || 10} hours</strong>.
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          Current total: {form.manualLessons.reduce((sum, lesson) => sum + lesson.duration, 0)} hours
                        </p>
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => {
                          setForm(f => ({
                            ...f,
                            manualLessons: [...f.manualLessons, { date: f.date, time: f.time, duration: 2 }]
                          }));
                        }}
                        className="w-full py-2 px-4 bg-drivewise-600 text-white rounded-lg hover:bg-drivewise-700 transition"
                      >
                        + Add Lesson
                      </button>

                      {form.manualLessons.length > 0 && (
                        <div className="space-y-2">
                          {form.manualLessons.map((lesson, index) => (
                            <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                              <div className="flex-1 grid grid-cols-3 gap-2">
                                <input
                                  type="date"
                                  value={lesson.date}
                                  onChange={e => {
                                    const newLessons = [...form.manualLessons];
                                    newLessons[index].date = e.target.value;
                                    setForm(f => ({ ...f, manualLessons: newLessons }));
                                  }}
                                  min={new Date().toISOString().split('T')[0]}
                                  className="px-2 py-1 border rounded text-sm"
                                />
                                <input
                                  type="time"
                                  value={lesson.time}
                                  onChange={e => {
                                    const newLessons = [...form.manualLessons];
                                    newLessons[index].time = e.target.value;
                                    setForm(f => ({ ...f, manualLessons: newLessons }));
                                  }}
                                  className="px-2 py-1 border rounded text-sm"
                                />
                                <select
                                  value={lesson.duration}
                                  onChange={e => {
                                    const newLessons = [...form.manualLessons];
                                    newLessons[index].duration = parseInt(e.target.value);
                                    setForm(f => ({ ...f, manualLessons: newLessons }));
                                  }}
                                  className="px-2 py-1 border rounded text-sm"
                                >
                                  <option value={1}>1 hour</option>
                                  <option value={2}>2 hours</option>
                                </select>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const newLessons = form.manualLessons.filter((_, i) => i !== index);
                                  setForm(f => ({ ...f, manualLessons: newLessons }));
                                }}
                                className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {errors.manualLessons && (
                        <div className="text-red-500 text-xs">{errors.manualLessons}</div>
                      )}
                      {errors.manualHours && (
                        <div className="text-red-500 text-xs">{errors.manualHours}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {errors.isRecurring && (
                <div className="text-red-500 text-xs mt-1">{errors.isRecurring}</div>
              )}
              {errors.recurringAvailability && (
                <div className="text-red-500 text-xs mt-1 bg-red-50 p-2 rounded border border-red-200">
                  {errors.recurringAvailability}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Car Type Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Car Type</label>
        <select
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.carType ? 'border-red-500' : 'border-gray-300'}`}
          value={form.carType}
          onChange={e => setForm(f => ({ ...f, carType: e.target.value }))}
        >
          <option value="">Select car type</option>
          {CAR_TYPES.map(type => (
            <option key={type} value={type}>{type}</option>
          ))}
        </select>
        {errors.carType && <div className="text-red-500 text-xs mt-1">{errors.carType}</div>}
      </div>

      {/* Area Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Area</label>
        <select
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.area ? 'border-red-500' : 'border-gray-300'}`}
          value={form.area}
          onChange={e => {
            setForm(f => ({ ...f, area: e.target.value, instructorId: '', instructorName: '', time: '' }));
            setTimeOverlapError(''); // Clear overlap error when area changes
          }}
          disabled={loadingAreas}
        >
          <option value="">{loadingAreas ? 'Loading areas...' : 'Select an area'}</option>
          {areas.map(area => (
            <option key={area.id} value={area.name}>{area.name}</option>
          ))}
        </select>
        {errors.area && <div className="text-red-500 text-xs mt-1">{errors.area}</div>}
      </div>

      {/* Instructor Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Instructor</label>
        <select
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.instructorId ? 'border-red-500' : 'border-gray-300'}`}
          value={form.instructorId}
          onChange={e => {
            const selectedInstructor = instructors.find(i => i.id === e.target.value);
            setForm(f => ({ 
              ...f, 
              instructorId: e.target.value, 
              instructorName: selectedInstructor ? `${selectedInstructor.firstName} ${selectedInstructor.lastName}` : '',
              time: '' // Clear time when instructor changes
            }));
            setTimeOverlapError(''); // Clear overlap error when instructor changes
          }}
          disabled={loadingInstructors || !form.area}
        >
          <option value="">{loadingInstructors ? 'Loading instructors...' : form.area ? 'Select an instructor' : 'Please select an area first'}</option>
          {instructors.map(instructor => (
            <option key={instructor.id} value={instructor.id}>{instructor.firstName} {instructor.lastName}</option>
          ))}
        </select>
        {errors.instructorId && <div className="text-red-500 text-xs mt-1">{errors.instructorId}</div>}
        {form.area && instructors.length === 0 && !loadingInstructors && (
          <div className="text-orange-500 text-xs mt-1">No instructors available in this area</div>
        )}
      </div>

      {/* Date Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Date of the Lesson</label>
        <input
          type="date"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.date ? 'border-red-500' : 'border-gray-300'}`}
          value={form.date}
          onChange={e => {
            setForm(f => ({ ...f, date: e.target.value, time: '' }));
            setTimeOverlapError(''); // Clear overlap error when date changes
          }}
          min={new Date().toISOString().split('T')[0]}
        />
        {errors.date && <div className="text-red-500 text-xs mt-1">{errors.date}</div>}
      </div>

      {/* Time Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Select Time of the Lesson</label>
        <select
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.time ? 'border-red-500' : 'border-gray-300'}`}
          value={form.time}
          onChange={async (e) => {
            const selectedTime = e.target.value;
            setForm(f => ({ ...f, time: selectedTime }));
            
            // Clear previous overlap error
            setTimeOverlapError('');
            
            // Check for overlaps if a time is selected
            if (selectedTime) {
              const overlapCheck = await checkTimeSlotOverlap(selectedTime);
              if (overlapCheck.hasOverlap) {
                setTimeOverlapError(overlapCheck.reason);
              }
            }
          }}
          disabled={!form.date || !form.instructorId || checkingAvailability}
        >
          <option value="">
            {checkingAvailability ? 'Checking availability...' : 
             !form.date ? 'Please select a date first' :
             !form.instructorId ? 'Please select an instructor first' :
             availableTimes.length === 0 ? 'No available times for this date' :
             'Select a time'}
          </option>
          {availableTimes.map(time => (
            <option key={time} value={time}>{time}</option>
          ))}
        </select>
        {errors.time && <div className="text-red-500 text-xs mt-1">{errors.time}</div>}
        {errors.timeOverlap && <div className="text-red-500 text-xs mt-1">{errors.timeOverlap}</div>}
        {availabilityError && <div className="text-red-500 text-xs mt-1">{availabilityError}</div>}
        {timeOverlapError && <div className="text-red-500 text-xs mt-1">{timeOverlapError}</div>}
        {availableTimes.length > 0 && !checkingAvailability && (
          <div className="text-green-600 text-xs mt-1">✓ {availableTimes.length} time slot{availableTimes.length !== 1 ? 's' : ''} available</div>
        )}
        {availableTimes.length === 0 && !checkingAvailability && form.date && form.instructorId && (
          <div className="text-orange-600 text-xs mt-1">⚠️ No available time slots for this instructor on this date. Please try a different date or instructor.</div>
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Back
        </button>
        <button
          type="submit"
          disabled={!!timeOverlapError || (!!recurringAvailabilityError && !checkingRecurringAvailability)}
          className={`px-6 py-2 rounded-lg transition ${
            (timeOverlapError || (recurringAvailabilityError && !checkingRecurringAvailability))
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed' 
              : 'bg-drivewise-700 text-white hover:bg-drivewise-800'
          }`}
        >
          {timeOverlapError ? 'Fix Time Conflict' : 
           (recurringAvailabilityError && !checkingRecurringAvailability) ? 'Fix Recurring Conflicts' :
           checkingRecurringAvailability ? 'Checking Availability...' :
           'Next'}
        </button>
      </div>
    </form>
  );

  // Step 3 UI
  const renderStep3 = () => (
    <form
      onSubmit={e => {
        e.preventDefault();
        if (validateStep3()) setStep(4);
      }}
      className="space-y-6"
    >
      <div className="text-lg font-semibold mb-2 text-drivewise-700">
        Step 3 of 4: Final Booking Details
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
        <input
          type="text"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.pickupAddress ? 'border-red-500' : 'border-gray-300'}`}
          value={form.pickupAddress}
          onChange={e => setForm(f => ({ ...f, pickupAddress: e.target.value }))}
        />
        {errors.pickupAddress && <div className="text-red-500 text-xs mt-1">{errors.pickupAddress}</div>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Point</label>
        <input
          type="text"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.meetingPoint ? 'border-red-500' : 'border-gray-300'}`}
          value={form.meetingPoint}
          onChange={e => setForm(f => ({ ...f, meetingPoint: e.target.value }))}
          placeholder="Optional: Where would you like to meet?"
        />
        {errors.meetingPoint && <div className="text-red-500 text-xs mt-1">{errors.meetingPoint}</div>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
        <input
          type="text"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.postcode ? 'border-red-500' : 'border-gray-300'}`}
          value={form.postcode}
          onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))}
        />
        {errors.postcode && <div className="text-red-500 text-xs mt-1">{errors.postcode}</div>}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Telephone Number</label>
        <input
          type="tel"
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.phone ? 'border-red-500' : 'border-gray-300'}`}
          value={form.phone}
          onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
        />
        {errors.phone && <div className="text-red-500 text-xs mt-1">{errors.phone}</div>}
      </div>
      <div className="flex justify-between gap-2 pt-2">
        <button
          type="button"
          className="py-2 px-4 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow hover:bg-gray-200 transition"
          onClick={() => setStep(2)}
        >
          Back
        </button>
        <button
          type="submit"
          className="py-2 px-4 bg-gradient-to-r from-blue-600 to-drivewise-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-700 hover:to-drivewise-700 transition"
        >
          Next
        </button>
      </div>
    </form>
  );

  // Step 4 UI
  const renderStep4 = () => {
    try {
      return (
        <form
          onSubmit={async e => {
            e.preventDefault();
            if (!validateStep4()) return;
            // Default to Pay in Person
            try {
              await handlePublicBookingSubmit({ ...form, paymentStatus: 'pending' });
              setStep(5);
            } catch (error: any) {
              console.error('Booking submission error:', error);
              setErrors({ 
                submission: error.message || 'Booking failed. Please try again.' 
              });
              // Show error message to user
              if (error.message && error.message.includes('overlaps')) {
                setAvailabilityError(error.message);
              }
            }
          }}
          className="space-y-6"
        >
          <div className="text-lg font-semibold mb-2 text-drivewise-700">
            Step 4 of 5: Review & Register (Optional)
          </div>
          
          {/* Course Details and Pricing */}
          {selectedCourse && (
            <div className="bg-drivewise-50 rounded-lg p-4 border border-drivewise-100">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <h3 className="font-semibold text-drivewise-900">{selectedCourse.title}</h3>
                  <p className="text-sm text-drivewise-700">{selectedCourse.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-drivewise-700">
                    £{totalPrice.toFixed(2)}
                  </div>
                  {isIndividualCourse(selectedCourse) && (
                    <div className="text-sm text-drivewise-600">
                      £{coursePrice.toFixed(2)} per hour × {form.selectedHours} hour{form.selectedHours > 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium">Course Type:</span> {selectedCourse.type}
                </div>
                <div>
                  <span className="font-medium">Total Hours:</span> {isIndividualCourse(selectedCourse) ? form.selectedHours : (selectedCourse?.totalHours || 1)} hours
                </div>
                {form.isRecurring && (
                  <div className="col-span-2">
                    <span className="font-medium">Recurring Lessons:</span> {Math.ceil((selectedCourse.totalHours || 10) / 2)} sessions of 2 hours each
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 text-sm">
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Name:</span>
              <span>{form.firstName || '-'} {form.lastName || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Email:</span>
              <span>{form.email || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Service/Lesson:</span>
              <span>{form.courseName || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Car Type:</span>
              <span>{form.carType || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Area:</span>
              <span>{form.area || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Instructor:</span>
              <span>{form.instructorName || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Date:</span>
              <span>{form.date || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Time:</span>
              <span>{form.time || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Address:</span>
              <span>{form.pickupAddress || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Meeting Point:</span>
              <span>{form.meetingPoint || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Postcode:</span>
              <span>{form.postcode || '-'}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="font-medium text-gray-700">Phone:</span>
              <span>{form.phone || '-'}</span>
            </div>
          </div>
          <div className="flex justify-end mb-2">
            <button
              type="button"
              className="py-2 px-4 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow hover:bg-gray-200 transition"
              onClick={() => setStep(3)}
            >
              Edit
            </button>
          </div>

          {/* Registration Option */}
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-3 mb-3">
              <input
                type="checkbox"
                id="wantsToRegister"
                checked={form.wantsToRegister}
                onChange={e => setForm(f => ({ ...f, wantsToRegister: e.target.checked, password: e.target.checked ? f.password : '' }))}
                className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
              />
              <label htmlFor="wantsToRegister" className="text-sm font-medium text-green-800">
                Create an account to track your lessons and progress
              </label>
            </div>
            
            {form.wantsToRegister && (
              <div className="space-y-3">
                <div className="text-xs text-green-700 mb-2">
                  Creating an account will allow you to:
                  • Track your lesson progress
                  • View your lesson history
                  • Communicate with your instructor
                  • Book future lessons easily
                </div>
                <div>
                  <label className="block text-sm font-medium text-green-800 mb-1">
                    Create a Password
                  </label>
                  <input
                    type="password"
                    placeholder="Enter a secure password (min. 6 characters)"
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm ${errors.password ? 'border-red-500' : 'border-green-300'}`}
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  />
                  {errors.password && <div className="text-red-500 text-xs mt-1">{errors.password}</div>}
                </div>
                <div className="text-xs text-green-600">
                  Your account will be created using the email: <strong>{form.email}</strong>
                </div>
              </div>
            )}
            
          {errors.registration && (
            <div className="mt-2 text-red-500 text-sm bg-red-50 p-2 rounded border border-red-200">
              {errors.registration}
            </div>
          )}
          
          {errors.submission && (
            <div className="mt-2 text-red-500 text-sm bg-red-50 p-2 rounded border border-red-200">
              {errors.submission}
            </div>
          )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="acceptTerms"
              checked={form.acceptTerms}
              onChange={e => setForm(f => ({ ...f, acceptTerms: e.target.checked }))}
              className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="acceptTerms" className="text-sm text-gray-700">
              I accept the <a href="/terms" className="text-blue-600 underline">terms and conditions</a>
            </label>
          </div>
          {errors.acceptTerms && <div className="text-red-500 text-xs mt-1">{errors.acceptTerms}</div>}
          
          <div className="flex flex-col gap-3 pt-2">
            <button
              type="submit"
              disabled={isRegistering}
              className="w-full py-2 px-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg shadow-md hover:from-orange-600 hover:to-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRegistering ? 'Creating Account...' : 'Pay in Person'}
            </button>
            <button
              type="button"
              disabled={isRegistering}
              className="w-full py-2 px-4 bg-gradient-to-r from-blue-600 to-drivewise-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-700 hover:to-drivewise-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={async () => {
                if (!validateStep4()) return;
                // TODO: Integrate Stripe payment here
                try {
                  await handlePublicBookingSubmit({ ...form, paymentStatus: 'paid' });
                  setStep(5);
                } catch (error: any) {
                  console.error('Booking submission error:', error);
                  setErrors({ 
                    submission: error.message || 'Booking failed. Please try again.'
                  });
                  // Show error message to user
                  if (error.message && error.message.includes('overlaps')) {
                    setAvailabilityError(error.message);
                  }
                }
              }}
            >
              {isRegistering ? 'Creating Account...' : 'Pay Now'}
            </button>
          </div>
        </form>
      );
    } catch (error) {
      console.error('Error in renderStep4:', error);
      return <div>Error rendering step 4</div>;
    }
  };

  // Step 5: Confirmation
  const renderStep5 = () => (
    <div className="text-center space-y-6">
      <div className="text-2xl font-bold text-green-600">Booking Confirmed!</div>
      <div className="text-gray-700">
        Thank you for booking your driving lesson. We have received your details and will contact you soon.
      </div>
      
      {form.wantsToRegister && (
        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
          <div className="text-sm text-green-800 font-medium mb-2">
            🎉 Account Created Successfully!
          </div>
          <div className="text-xs text-green-700">
            Your account has been created with email: <strong>{form.email}</strong>
            <br />
            You can now sign in to track your lessons and communicate with your instructor.
          </div>
        </div>
      )}
      
      <div className="mt-4 space-y-2">
        {form.wantsToRegister && (
          <a 
            href="/login" 
            className="block text-blue-600 underline hover:text-blue-800 transition"
          >
            Sign in to your new account
          </a>
        )}
        <a href="/" className="block text-blue-600 underline hover:text-blue-800 transition">
          Return to Home
        </a>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-blue-50 to-white">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-lg w-full border border-blue-100 mt-12">
          <h1 className="text-2xl font-bold mb-4 text-drivewise-700">Book a Driving Lesson</h1>
          <p className="mb-8 text-gray-600">Start your journey by filling out the form below.</p>
          {/* Step indicator for debugging */}
          <div className="mb-4 text-center text-xs text-gray-400">Step {step} of 5</div>
          <div className="space-y-8">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
            {step === 5 && renderStep5()}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PublicBookLesson; 